/**
 * RoleProgramAccessDialog
 *
 * Role Setup → per-program member access. Lets an admin grant a role VIEW or
 * EDIT access to a program's members at the ROLE level (no per-user assignment
 * needed). Backed by:
 *   GET  /api/organizations/{orgId}/roles/{roleType}/programs
 *   PUT  /api/organizations/{orgId}/roles/{roleType}/programs
 * where access_level ∈ 'full' (view + edit) | 'view_only' (read) | 'none'.
 *
 * The role→program grant is enforced live in the backend (organization_role_programs)
 * and is the most-permissive companion to per-user program assignments.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/apiClient';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { RefreshCw, Save, AlertCircle, Eye, Edit3, Ban, FolderKanban } from 'lucide-react';
import type { OrganizationRole } from '@/types/roleTerminology';

type AccessLevel = 'none' | 'view_only' | 'full';

interface ProgramRow {
  program_id: number;
  program_name: string;
  access_level: AccessLevel;
}

interface RoleProgramAccessDialogProps {
  organizationId: number;
  role: OrganizationRole | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ACCESS_OPTIONS: { value: AccessLevel; label: string; hint: string }[] = [
  { value: 'none', label: 'No access', hint: "Role does not see this program's members" },
  { value: 'view_only', label: 'View only', hint: "Role can see (read-only) this program's members" },
  { value: 'full', label: 'View + edit', hint: "Role can see and edit this program's members" },
];

export function RoleProgramAccessDialog({
  organizationId,
  role,
  open,
  onOpenChange,
}: RoleProgramAccessDialogProps) {
  const [rows, setRows] = useState<ProgramRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cboHeader = { headers: { 'X-CBO-ID': organizationId.toString() } };

  const load = useCallback(async () => {
    if (!role) return;
    try {
      setLoading(true);
      setError(null);
      const [programsRes, configRes] = await Promise.all([
        apiClient.get(`/api/programs`, cboHeader),
        apiClient.get(
          `/api/organizations/${organizationId}/roles/${role.role_type}/programs`,
          cboHeader,
        ),
      ]);

      const allPrograms: { id: number; name: string }[] = programsRes.data || [];
      // Configured rows only include programs with a non-'none' access_level.
      const configured = new Map<number, AccessLevel>(
        (configRes.data || []).map((c: any) => [c.program_id, c.access_level as AccessLevel]),
      );

      setRows(
        allPrograms
          .map((p) => ({
            program_id: p.id,
            program_name: p.name,
            access_level: configured.get(p.id) ?? ('none' as AccessLevel),
          }))
          .sort((a, b) => a.program_name.localeCompare(b.program_name)),
      );
    } catch (err: any) {
      console.error('Error loading role program access:', err);
      setError(err.response?.data?.detail || 'Failed to load program access');
    } finally {
      setLoading(false);
    }
  }, [role, organizationId]);

  useEffect(() => {
    if (open && role) load();
  }, [open, role, load]);

  const setAccess = (programId: number, level: AccessLevel) => {
    setRows((prev) =>
      prev.map((r) => (r.program_id === programId ? { ...r, access_level: level } : r)),
    );
  };

  const handleSave = async () => {
    if (!role) return;
    try {
      setSaving(true);
      setError(null);
      await apiClient.put(
        `/api/organizations/${organizationId}/roles/${role.role_type}/programs`,
        {
          programs: rows.map((r) => ({
            program_id: r.program_id,
            access_level: r.access_level,
          })),
        },
        cboHeader,
      );
      onOpenChange(false);
    } catch (err: any) {
      console.error('Error saving role program access:', err);
      setError(err.response?.data?.detail || 'Failed to save program access');
    } finally {
      setSaving(false);
    }
  };

  const grantedCount = rows.filter((r) => r.access_level !== 'none').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderKanban className="w-5 h-5" />
            Program access — {role?.display_name_en || role?.role_type}
          </DialogTitle>
          <DialogDescription>
            Grant this role <strong>view</strong> or <strong>edit</strong> access to a program&apos;s
            members. Everyone with this role gets the access below without a per-user assignment.
            Only applies to programs using a shared caseload.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading programs…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No programs found for this organization.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
              <span>{rows.length} program{rows.length === 1 ? '' : 's'}</span>
              <Badge variant="secondary">{grantedCount} granted</Badge>
            </div>
            {rows.map((r) => (
              <div
                key={r.program_id}
                className="flex items-center justify-between gap-3 rounded-md border p-3"
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{r.program_name}</p>
                </div>
                <Select
                  value={r.access_level}
                  onValueChange={(v) => setAccess(r.program_id, v as AccessLevel)}
                >
                  <SelectTrigger className="w-44 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCESS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-2">
                          {opt.value === 'none' && <Ban className="w-3.5 h-3.5 text-muted-foreground" />}
                          {opt.value === 'view_only' && <Eye className="w-3.5 h-3.5" />}
                          {opt.value === 'full' && <Edit3 className="w-3.5 h-3.5" />}
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading || rows.length === 0}>
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RoleProgramAccessDialog;
